import type { IntlShape } from "@formatjs/intl";
import { Descriptions, Flex, Tag, Typography } from "antd";
import { formatComponentLabel, isBlade } from "../../../../common/utils/componentLabel";
import type { Services, TicketItemDetailed } from "../../../generated-axios-client";
import { ReportPicturePicker } from "./ReportPicturePicker";
import type { ReportPicture } from "./reportPictures";

type ReportTaskStepProps = {
  task: TicketItemDetailed;
  position: number;
  taskCount: number;
  pictures: ReportPicture[];
  selectedFileIds: string[];
  onChange: (selectedFileIds: string[]) => void;
  serviceObjects: Record<string, Services>;
  intl: IntlShape;
};

export function ReportTaskStep({
  task,
  position,
  taskCount,
  pictures,
  selectedFileIds,
  onChange,
  serviceObjects,
  intl,
}: ReportTaskStepProps) {
  const component = task.component;
  const taskLabel =
    task.ticket_item_number == null
      ? intl.formatMessage({ id: "wo_report_task_title_unnumbered" })
      : intl.formatMessage(
          { id: "wo_report_task_title" },
          { number: task.ticket_item_number }
        );
  const title = component
    ? `${taskLabel} · ${formatComponentLabel(component)}`
    : taskLabel;

  const details = [
    {
      key: "service",
      label: intl.formatMessage({ id: "ticket_service" }),
      children: (task.service && serviceObjects[task.service]?.name) || "—",
    },
    ...(isBlade(component)
      ? [
          {
            key: "set",
            label: intl.formatMessage({ id: "wo_report_task_set_number" }),
            children: component?.set_number || "—",
          },
        ]
      : []),
    ...(component?.model
      ? [
          {
            key: "model",
            label: intl.formatMessage({ id: "wo_report_task_model" }),
            children: component.model,
          },
        ]
      : []),
    {
      key: "description",
      label: intl.formatMessage({ id: "wo_report_task_description" }),
      children: task.description || "—",
    },
  ];

  return (
    <Flex vertical gap="middle">
      <Flex vertical gap="small">
        <Typography.Text type="secondary">
          {intl.formatMessage(
            { id: "wo_report_task_position" },
            { position, count: taskCount }
          )}
        </Typography.Text>
        <Flex align="center" justify="space-between" gap="small" wrap="wrap">
          <Typography.Title level={5}>{title}</Typography.Title>
          <Tag color={task.completed ? "success" : "default"}>
            {intl.formatMessage({
              id: task.completed
                ? "wo_report_task_completed"
                : "wo_report_task_not_completed",
            })}
          </Tag>
        </Flex>
        <Descriptions size="small" column={1} items={details} />
      </Flex>
      <ReportPicturePicker
        pictures={pictures}
        selectedFileIds={selectedFileIds}
        onChange={onChange}
        emptyText={intl.formatMessage({ id: "wo_report_task_no_pictures" })}
        intl={intl}
      />
    </Flex>
  );
}
